// Invoice PDF generator.
//
// Draws the invoice as real vector text via jsPDF (not a screenshot), so
// it stays sharp at any zoom and the file stays small — usually under
// 100KB even with the logo embedded.
//
// Requires: npm install jspdf

import { jsPDF } from "jspdf";

// A4 portrait, millimetres.
const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 20;

// Palette sampled from the Canva design.
const CREAM_TOP = [253, 241, 236];   // soft pink, upper section
const CREAM_BOTTOM = [250, 250, 233]; // pale yellow, lower section
const INK = [26, 26, 26];
const MUTED = [110, 105, 100];
const RULE = [214, 206, 198];

const BUSINESS = {
  name: "Ratnapura Gems",
  addressLines: ["59/A, Town Center Building,", "Colombo Road, Kurunegala"],
  phones: ["076-5851400", "071-3880388"],
  website: "www.ratnapuragems.lk",
  instagram: "@ratnapuragems",
};

const NOTE =
  "Note: This invoice includes a lifetime warranty for the listed gemstones and base metal";

function money(n) {
  return `${Number(n || 0).toLocaleString("en-US")} LKR`;
}

function formatDate(iso) {
  const d = iso ? new Date(iso) : new Date();
  if (isNaN(d.getTime())) return "";
  const pad = (x) => String(x).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

// Derive the status label from what's been paid.
export function paymentStatus(order) {
  const total = Number(order.itemPrice || 0) + Number(order.certificateFee || 0);
  const paid = Number(order.advancePaid || 0);
  if (total > 0 && paid >= total) return "Paid in Full";
  if (paid > 0) return "Advance Paid";
  return "Payment Pending";
}

// Load the logo as a data URL so jsPDF can embed it.
// Returns null if unavailable — the invoice still renders without it.
async function loadLogo() {
  try {
    const res = await fetch("/logo.jpg");
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export async function generateInvoice(order, { download = true } = {}) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });

  const itemPrice = Number(order.itemPrice || 0);
  const certFee = Number(order.certificateFee || 0);
  const advance = Number(order.advancePaid || 0);
  const balance = itemPrice + certFee - advance;

  // ── Backgrounds ──
  const SPLIT_Y = 104; // where the pink section gives way to the cream one
  doc.setFillColor(...CREAM_TOP);
  doc.rect(0, 0, PAGE_W, SPLIT_Y, "F");
  doc.setFillColor(...CREAM_BOTTOM);
  doc.rect(0, SPLIT_Y, PAGE_W, PAGE_H - SPLIT_Y, "F");

  // ── Logo ──
  const logo = await loadLogo();
  if (logo) {
    try {
      doc.addImage(logo, "JPEG", MARGIN + 4, 14, 42, 42, undefined, "FAST");
    } catch {
      /* corrupt image — carry on without it */
    }
  }

  // ── "INVOICE" heading ──
  doc.setTextColor(...INK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(34);
  doc.text("INVOICE", PAGE_W - MARGIN, 36, { align: "right" });

  // ── Left detail block ──
  let y = 68;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  doc.setTextColor(...INK);
  doc.text(order.productName || "—", MARGIN, y);
  y += 8;
  doc.text(`Billed To: ${order.customerName || "—"}`, MARGIN, y);
  y += 8;
  doc.text(order.customerPhone || "—", MARGIN, y);

  // ── Right detail block ──
  let ry = 68;
  doc.setFontSize(12);
  doc.text(`Invoice No. ${order.invoiceNumber ?? "—"}`, PAGE_W - MARGIN, ry, { align: "right" });
  ry += 8;
  doc.text(`Issue Date: ${formatDate(order.createdAt)}`, PAGE_W - MARGIN, ry, { align: "right" });
  ry += 8;
  doc.text(`Status: ${paymentStatus(order)}`, PAGE_W - MARGIN, ry, { align: "right" });

  // ── Divider between header and body ──
  doc.setDrawColor(...RULE);
  doc.setLineWidth(0.4);
  doc.line(MARGIN - 8, 98, PAGE_W - MARGIN + 8, 98);

  // ── Column headings ──
  y = 128;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(15);
  doc.setTextColor(...INK);
  doc.text("D E S C R I P T I O N", MARGIN, y);
  doc.text("S U B T O T A L", PAGE_W - MARGIN, y, { align: "right" });

  // ── Line items ──
  y += 14;
  doc.setFontSize(11);

  const rows = [[order.productName || "Item", itemPrice]];
  if (certFee > 0) {
    rows.push([order.certificateLabel || "Full gem report", certFee]);
  }
  if (advance > 0) {
    rows.push(["Advance Payment", advance]);
  }

  for (const [label, amount] of rows) {
    doc.setTextColor(...INK);
    doc.text(String(label), MARGIN, y);
    doc.text(money(amount), PAGE_W - MARGIN, y, { align: "right" });
    y += 8;
  }

  // ── Balance ──
  y += 6;
  doc.setDrawColor(...RULE);
  doc.setLineWidth(0.8);
  doc.line(MARGIN, y, PAGE_W - MARGIN, y);
  y += 9;
  doc.setFontSize(11);
  doc.text("Balance", MARGIN, y);
  doc.text(money(balance), PAGE_W - MARGIN, y, { align: "right" });

  // ── Footer: business details ──
  const footY = 232;
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...INK);
  doc.text(BUSINESS.name, MARGIN, footY);

  doc.setFont("helvetica", "normal");
  let fy = footY + 6;
  for (const line of BUSINESS.addressLines) {
    doc.text(line, MARGIN, fy);
    fy += 6;
  }
  for (const phone of BUSINESS.phones) {
    doc.text(phone, MARGIN, fy);
    fy += 6;
  }

  // ── Footer: web + social ──
  doc.setFontSize(10);
  doc.text(BUSINESS.website, PAGE_W - MARGIN, footY + 4, { align: "right" });
  doc.text(`visit us on  ${BUSINESS.instagram}`, PAGE_W - MARGIN, footY + 11, { align: "right" });

  doc.setFontSize(17);
  doc.text("T H A N K   Y O U .", PAGE_W - MARGIN, footY + 26, { align: "right" });

  // ── Note ──
  doc.setFont("helvetica", "italic");
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  doc.text(NOTE, PAGE_W / 2, 282, { align: "center", maxWidth: PAGE_W - 2 * MARGIN });

  const filename = `Invoice-${order.invoiceNumber ?? order.trackingId}.pdf`;
  if (download) {
    doc.save(filename);
  }
  return { doc, filename };
}